import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPaymentHistory,
  fetchSubscriptionStatus,
  cancelSubscription,
  reactivateSubscription
} from '../../../features/payment/paymentSlice';

export const SubscriptionSettings = () => {
  const dispatch = useDispatch();
  const {
    paymentHistory,
    paymentSubscription,
    paymentHistoryLoading,
    subscriptionStatus,
    subscriptionStatusLoading,
    cancelLoading,
    reactivateLoading,
    error
  } = useSelector((state) => state.payment);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchPaymentHistory());
    dispatch(fetchSubscriptionStatus());
  }, [dispatch]);

  const handleCancelSubscription = async () => {
    try {
      await dispatch(cancelSubscription()).unwrap();
      setShowCancelConfirm(false);
      setSuccessMessage('Subscription cancelled successfully.');
      dispatch(fetchSubscriptionStatus());
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await dispatch(reactivateSubscription()).unwrap();
      setShowReactivateConfirm(false);
      if (isScheduledForCancellation) {
        setSuccessMessage('Subscription continued successfully. Your subscription will renew normally.');
      } else {
        setSuccessMessage('Subscription reactivated successfully.');
      }
      dispatch(fetchSubscriptionStatus());
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatAmount = (amount, currency) => {
    if (!amount) return '-';
    const symbol = currency === 'USD' ? '$' : currency === 'AUD' ? 'A$' : currency || '$';
    return `${symbol}${amount}`;
  };

  const getPlanName = () => {
    return subscriptionStatus?.subscription?.planName || subscriptionStatus?.subscription?.plan || subscriptionStatus?.subscriptionPlan || '-';
  };

  const getPlanPrice = () => {
    const amount = subscriptionStatus?.subscription?.amount;
    if (!amount) return '';
    return ` - $${amount}/month`;
  };

  const getSubscriptionStatusText = () => {
    const cancelAtPeriodEnd = subscriptionStatus?.subscription?.cancelAtPeriodEnd;
    const status = subscriptionStatus?.subscription?.status || subscriptionStatus?.subscriptionStatus || '-';

    if (cancelAtPeriodEnd) {
      return 'Cancelled (Active until period end)';
    }
    return status;
  };

  const getNextBillingDate = () => {
    const date = subscriptionStatus?.subscription?.renewalDate || subscriptionStatus?.subscription?.currentPeriodEnd;
    return date ? formatDate(date) : '-';
  };

  const getDaysRemaining = () => {
    return paymentSubscription?.daysRemaining ?? null;
  };

  const getCancelledDate = () => {
    const date = subscriptionStatus?.subscription?.canceledAt;
    return date ? formatDate(date) : null;
  };

  // Check if subscription is scheduled to be cancelled
  const isScheduledForCancellation = subscriptionStatus?.subscription?.cancelAtPeriodEnd === true;

  // Check if subscription is completely cancelled (not active)
  const isSubscriptionCancelled = getSubscriptionStatusText().toLowerCase().includes('cancelled') ||
                                   getSubscriptionStatusText().toLowerCase().includes('canceled');

  // currentState from API — single source of truth
  const currentState = subscriptionStatus?.currentState;

  return (
    <div className="w-full bg-[#121826] rounded-2xl shadow-xl mt-4 p-4 md:p-8">
      <section className="text-left">
        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-500/20 border border-green-500/50 rounded-lg flex items-center gap-3">
            <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-600 dark:text-green-400 font-medium">{successMessage}</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-3">
            <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-600 dark:text-red-400 font-medium">{error}</span>
          </div>
        )}

        {/* Loading State */}
        {subscriptionStatusLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Plan & Controls */}
            <h2 className="text-2xl font-bold text-jp-ink mb-4 text-left">CURRENT PLAN</h2>

            <div className="bg-jp-section border border-jp-line rounded-lg p-5 mb-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-jp-muted text-sm mb-1">Plan</p>
                  <p className="text-jp-ink text-lg font-semibold uppercase">
                    {getPlanName()}<span className="text-jp-accent-ink">{getPlanPrice()}</span>
                  </p>
                </div>

                <div>
                  <p className="text-jp-muted text-sm mb-1">Status</p>
                  <p className="text-jp-ink text-lg font-semibold">
                    <span className={`${isScheduledForCancellation ? 'text-orange-600 dark:text-orange-400' : isSubscriptionCancelled ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {getSubscriptionStatusText()}
                    </span>
                  </p>
                </div>

                <div>
                  <p className="text-jp-muted text-sm mb-1">
                    {isScheduledForCancellation ? 'Active Until' : 'Next Billing Date'}
                  </p>
                  <p className="text-jp-ink text-lg font-semibold">{getNextBillingDate()}</p>
                </div>

                {getDaysRemaining() !== null && (
                  <div>
                    <p className="text-jp-muted text-sm mb-1">Days Remaining</p>
                    <p className="text-jp-ink text-lg font-semibold">{getDaysRemaining()} days</p>
                  </div>
                )}

                {getCancelledDate() && (
                  <div>
                    <p className="text-jp-muted text-sm mb-1">Cancelled On</p>
                    <p className="text-jp-ink text-lg font-semibold">{getCancelledDate()}</p>
                  </div>
                )}
              </div>

              {/* Features List */}
              {subscriptionStatus?.subscription?.features && subscriptionStatus.subscription.features.length > 0 && (
                <div className="mt-5 pt-5 border-t border-jp-line">
                  <p className="text-jp-muted text-sm mb-3">Plan Features:</p>
                  <ul className="space-y-2">
                    {subscriptionStatus.subscription.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-jp-muted text-sm">
                        <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="text-left">
              {currentState === 'pending_cancel' ? (
                <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-amber-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-amber-600 dark:text-yellow-400 font-semibold text-sm">Cancellation Request Pending</p>
                      <p className="text-jp-muted text-sm mt-1">Your cancellation request has been submitted and is under review. You cannot submit another request at this time.</p>
                    </div>
                  </div>
                </div>
              ) : currentState === 'cancelled' ? (
                <div className="bg-orange-500/10 border border-orange-500/50 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3 mb-4">
                    <svg className="w-6 h-6 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-orange-600 dark:text-orange-400 font-semibold mb-1">Cancellation Approved</p>
                      <p className="text-jp-muted text-sm">Your cancellation has been approved. Your subscription will remain active until <span className="text-jp-ink font-semibold">{getNextBillingDate()}</span>. You can reactivate anytime before this date.</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowReactivateConfirm(true)}
                    disabled={reactivateLoading}
                    className="bg-green-600 hover:bg-green-500 text-white px-6 py-2 rounded-lg font-semibold focus:outline-none shadow transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {reactivateLoading && <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                    Reactivate Subscription
                  </button>
                </div>
              ) : currentState === 'pending_reactivation' ? (
                <div className="flex items-start gap-3 bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4">
                  <svg className="w-5 h-5 text-amber-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-amber-600 dark:text-yellow-400 font-semibold text-sm">Reactivation Request Pending</p>
                    <p className="text-jp-muted text-sm mt-1">Your reactivation request has been submitted and is under review.</p>
                  </div>
                </div>
              ) : isScheduledForCancellation ? (
                <div className="bg-orange-500/10 border border-orange-500/50 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3 mb-4">
                    <svg className="w-6 h-6 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-orange-600 dark:text-orange-400 font-semibold mb-1">Subscription Scheduled for Cancellation</p>
                      <p className="text-jp-muted text-sm">Your subscription will remain active until <span className="text-jp-ink font-semibold">{getNextBillingDate()}</span>. You can reactivate it anytime before this date.</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowReactivateConfirm(true)}
                    disabled={reactivateLoading}
                    className="bg-green-600 hover:bg-green-500 text-white px-6 py-2 rounded-lg font-semibold focus:outline-none shadow transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {reactivateLoading && <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                    Continue Subscription
                  </button>
                </div>
              ) : isSubscriptionCancelled ? (
                <button
                  onClick={() => setShowReactivateConfirm(true)}
                  disabled={reactivateLoading}
                  className="bg-green-600 hover:bg-green-500 text-white px-6 py-2 rounded-lg font-semibold focus:outline-none shadow transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {reactivateLoading && <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Reactivate Subscription
                </button>
              ) : (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={cancelLoading}
                  className="bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded-lg font-semibold focus:outline-none shadow transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {cancelLoading && <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Cancel Subscription
                </button>
              )}
            </div>
          </>
        )}

        {/* Recent Payments / Billing History */}
        <div className="mt-8">
          <h3 className="text-jp-ink text-sm font-bold mb-2 uppercase tracking-wide">Billing History</h3>

          {paymentHistoryLoading ? (
            <div className="flex items-center justify-center py-8 border border-jp-line rounded-lg">
              <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : paymentHistory && paymentHistory.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-jp-line">
              <table className="min-w-full text-left text-jp-ink text-sm">
                <thead>
                  <tr className="bg-[#192132] border-b border-jp-line">
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Amount</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Description</th>
                    <th className="px-4 py-3 font-medium">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment, idx) => (
                    <tr key={payment.id || idx} className="border-b border-jp-line hover:bg-jp-line transition">
                      <td className="px-4 py-3">{formatDate(payment.date)}</td>
                      <td className="px-4 py-3">{formatAmount(payment.amount, payment.currency)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          payment.status === 'paid' ? 'bg-green-500/20 text-green-600 dark:text-green-400' :
                          payment.status === 'pending' ? 'bg-yellow-500/20 text-amber-600 dark:text-yellow-400' :
                          'bg-red-500/20 text-red-600 dark:text-red-400'
                        }`}>
                          {payment.status ? payment.status.toUpperCase() : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">{payment.description || '-'}</td>
                      <td className="px-4 py-3">
                        {payment.invoiceUrl ? (
                          <a
                            href={payment.invoiceUrl}
                            download={`invoice-${payment.id}.pdf`}
                            className="text-jp-accent-ink hover:text-jp-accent underline cursor-pointer inline-flex items-center gap-1"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                            </svg>
                            Download
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : paymentSubscription ? (
            <div className="overflow-x-auto rounded-lg border border-jp-line">
              <table className="min-w-full text-left text-jp-ink text-sm">
                <thead>
                  <tr className="bg-[#192132] border-b border-jp-line">
                    <th className="px-4 py-3 font-medium">Billing Period</th>
                    <th className="px-4 py-3 font-medium">Plan</th>
                    <th className="px-4 py-3 font-medium">Amount</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Provider</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-jp-line hover:bg-jp-line transition">
                    <td className="px-4 py-3">
                      {formatDate(paymentSubscription.currentPeriodStart)} - {formatDate(paymentSubscription.currentPeriodEnd)}
                    </td>
                    <td className="px-4 py-3">{paymentSubscription.plan || '-'}</td>
                    <td className="px-4 py-3">
                      {subscriptionStatus?.subscription?.amount
                        ? `$${subscriptionStatus.subscription.amount}`
                        : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        paymentSubscription.isActive ? 'bg-green-500/20 text-green-600 dark:text-green-400' :
                        'bg-red-500/20 text-red-600 dark:text-red-400'
                      }`}>
                        {paymentSubscription.status?.toUpperCase() || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 capitalize">{paymentSubscription.paymentProvider || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center border border-jp-line rounded-lg">
              <p className="text-jp-muted">No billing history found.</p>
            </div>
          )}
        </div>

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-jp-section rounded-xl p-6 max-w-md mx-4">
              <h3 className="text-xl font-bold text-jp-ink mb-4">Cancel Subscription</h3>
              <p className="text-jp-muted mb-6">
                Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="px-4 py-2 bg-jp-section hover:bg-jp-line text-jp-ink rounded-lg transition"
                >
                  No, Keep It
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {cancelLoading && (
                    <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  Yes, Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reactivate Confirmation Modal */}
        {showReactivateConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-jp-section rounded-xl p-6 max-w-md mx-4">
              <h3 className="text-xl font-bold text-jp-ink mb-4">
                {isScheduledForCancellation ? 'Continue Subscription' : 'Reactivate Subscription'}
              </h3>
              <p className="text-jp-muted mb-6">
                {isScheduledForCancellation
                  ? 'Would you like to continue your subscription? Your cancellation will be reversed and your subscription will continue normally.'
                  : 'Would you like to reactivate your subscription? You will regain access to all premium features.'}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowReactivateConfirm(false)}
                  className="px-4 py-2 bg-jp-section hover:bg-jp-line text-jp-ink rounded-lg transition"
                >
                  No, Go Back
                </button>
                <button
                  onClick={handleReactivateSubscription}
                  disabled={reactivateLoading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {reactivateLoading && (
                    <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {isScheduledForCancellation ? 'Yes, Continue' : 'Yes, Reactivate'}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};
